import { useCallback, useMemo, useRef, useState } from 'react';
import { Checkbox } from '@amzn/stencil-react-components/checkbox';
import { LabelPosition } from '@amzn/stencil-react-components/form';
import { Input, InputWrapper } from '@amzn/stencil-react-components/input';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import {
  PopoverController,
  PopoverPosition,
} from '@amzn/stencil-react-components/popover';
import { Text } from '@amzn/stencil-react-components/text';
import { IconChevronDownSmall, IconSearchSmall } from '@amzn/stencil-react-icons';

import { computeMasterCheckboxState, toggleScopedSelection } from '../../forms';
import { ChipTokenGroup, type ChipTokenItem } from './ChipTokenGroup';
import { decodeTagPair, type TagLeafOption, type TagOptionGroup } from './tagOptionCodec';

/**
 * Above this many leaf options the in-popover search field is shown ("don't
 * use searchable for fewer than ten items" — the same rule the flat
 * {@link OptionSelect} / {@link OptionMultiSelect} apply).
 */
const SEARCHABLE_THRESHOLD = 10;

export interface GroupedOptionMultiSelectProps {
  /**
   * Grouped options (tag group → values). Rendered as a group-header checkbox
   * (tri-state: checked / indeterminate / unchecked) that selects or clears
   * every value in the group, with the group's values indented beneath —
   * matching the manual question workflow's grouped tag picker.
   */
  readonly optionGroups: readonly TagOptionGroup[];
  /** Currently-selected leaf `value`s (encoded). Values matching no leaf are
   *  ignored rather than rendered as phantom selections. */
  readonly values: readonly string[];
  /** Emits the full new set of selected leaf `value`s on every toggle. */
  readonly onChange: (values: string[]) => void;
  readonly placeholder?: string | undefined;
  readonly disabled?: boolean | undefined;
  readonly hasError?: boolean | undefined;
  readonly ariaLabel?: string | undefined;
  /** Sets `aria-required` on the trigger. Visual asterisk is
   *  {@link RequiredLabel}'s job. */
  readonly isRequired?: boolean | undefined;
  /**
   * Accessible base label for each selected-tag chip's dismiss (×) control,
   * e.g. "Remove". When supplied, the selected tags render as dismissible
   * two-line chips (value + group) beneath the trigger — matching the manual
   * flow. When omitted, no chip row is shown.
   */
  readonly chipDismissAltText?: string | undefined;
  readonly dataTestId?: string | undefined;
}

/** Case-insensitive leaf-label filter, preserving group structure and dropping
 *  groups left with no matching leaf. */
function filterGroups(
  groups: readonly TagOptionGroup[],
  query: string,
): readonly TagOptionGroup[] {
  const trimmed = query.trim().toLocaleLowerCase();
  if (!trimmed) return groups;
  const result: TagOptionGroup[] = [];
  for (const group of groups) {
    const matching = group.values.filter((leaf) =>
      leaf.label.toLocaleLowerCase().includes(trimmed),
    );
    if (matching.length > 0) {
      result.push({ groupName: group.groupName, values: matching });
    }
  }
  return result;
}

/** Builds the trigger's visible text: the selected count, or the placeholder
 *  when nothing is selected. */
function triggerText(count: number, placeholder: string | undefined): string {
  if (count === 0) return placeholder ?? '';
  return `${count} selected`;
}

/**
 * Shared grouped multi-select rendered as a custom Stencil popover. Unlike the
 * plain Stencil `Select` (whose group headers are non-interactive labels), each
 * group header here is a tri-state checkbox that toggles its whole group — the
 * affordance the manual question workflow provides.
 *
 * `values` / `onChange` operate on the flat list of encoded leaf `value`s; the
 * caller pairs this with the {@link tagOptionCodec} helpers to map to/from its
 * own stored shape (e.g. a `TagMap`). Selection is emitted immediately on every
 * toggle (no staged draft) — consistent with the sibling selects in the
 * question-config card.
 *
 * Deliberately renders NO label — pair with {@link RequiredLabel}, same as the
 * flat selects. The per-group "select all" math is the shared
 * {@link computeMasterCheckboxState} / {@link toggleScopedSelection} primitive,
 * so it stays identical to the flat master-checkbox behavior elsewhere.
 */
export function GroupedOptionMultiSelect(
  props: GroupedOptionMultiSelectProps,
): JSX.Element {
  const {
    optionGroups,
    values,
    onChange,
    placeholder,
    disabled,
    hasError,
    ariaLabel,
    isRequired,
    chipDismissAltText,
    dataTestId,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Width of the trigger, captured on open so the popover hugs the field
  // (matching the sibling Stencil Select dropdowns, which size to their
  // trigger) instead of rendering as an arbitrary-width card.
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>(undefined);
  const triggerRef = useRef<HTMLInputElement>(null);

  const leafValues = useMemo(
    () => optionGroups.flatMap((group) => group.values.map((leaf) => leaf.value)),
    [optionGroups],
  );

  // Only values that still correspond to a real leaf count toward the trigger —
  // a stale / deactivated value shouldn't inflate the "N selected" text.
  const selectedCount = useMemo(() => {
    const known = new Set(leafValues);
    return values.filter((value) => known.has(value)).length;
  }, [leafValues, values]);

  const visibleGroups = useMemo(
    () => filterGroups(optionGroups, query),
    [optionGroups, query],
  );

  // Selected tags as two-line chips (value label on top, group name beneath) —
  // matching the manual flow. Built from the leaf options so the chip shows the
  // same display label as the popover; the group comes from decoding the
  // encoded value. Unknown values (no matching leaf) are dropped, mirroring the
  // trigger count. Memoised for the reference-stable-items contract.
  const chipItems = useMemo<readonly ChipTokenItem[]>(() => {
    const leafByValue = new Map(
      optionGroups.flatMap((group) => group.values).map((leaf) => [leaf.value, leaf]),
    );
    const items: ChipTokenItem[] = [];
    for (const value of values) {
      const leaf = leafByValue.get(value);
      if (!leaf) continue;
      const pair = decodeTagPair(value);
      items.push({
        value,
        label: leaf.label,
        ...(pair ? { description: pair.group } : {}),
      });
    }
    return items;
  }, [optionGroups, values]);

  const showSearch = leafValues.length >= SEARCHABLE_THRESHOLD;

  const closePopover = useCallback(() => {
    setIsOpen(false);
    setQuery('');
  }, []);

  // Capture the trigger's current width so the popover matches it, then open.
  const openPopover = useCallback(() => {
    const width = triggerRef.current?.getBoundingClientRect().width;
    if (width) setTriggerWidth(width);
    setIsOpen(true);
    setQuery('');
  }, []);

  const toggleOpen = useCallback(() => {
    if (isOpen) closePopover();
    else openPopover();
  }, [isOpen, closePopover, openPopover]);

  // The trigger is a read-only Input (field-shaped, matching the sibling
  // OptionSelect fields on the card). Keyboard users open it with
  // Enter / Space / ArrowDown, mirroring a native select trigger.
  const handleTriggerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      if (disabled) return;
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        openPopover();
      } else if (event.key === 'Escape') {
        closePopover();
      }
    },
    [disabled, openPopover, closePopover],
  );

  const toggleLeaf = useCallback(
    (leafValue: string): void => {
      const next = new Set(values);
      if (next.has(leafValue)) next.delete(leafValue);
      else next.add(leafValue);
      onChange(Array.from(next));
    },
    [values, onChange],
  );

  const toggleGroup = useCallback(
    (group: TagOptionGroup): void => {
      const scoped = group.values.map((leaf) => leaf.value);
      onChange(toggleScopedSelection(scoped, values));
    },
    [values, onChange],
  );

  const selectedSet = useMemo(() => new Set(values), [values]);
  const testId = dataTestId ?? 'grouped-multi-select';

  // Selected-count summary shown IN the field when something is selected; the
  // placeholder shows through (like the sibling selects) when nothing is.
  const summary = selectedCount === 0 ? '' : triggerText(selectedCount, placeholder);

  return (
    <Col gridGap="dimensions.spacing.200">
      <Input
        ref={triggerRef}
        readOnly
        role="combobox"
        value={summary}
        onClick={disabled ? undefined : toggleOpen}
        onKeyDown={handleTriggerKeyDown}
        insetElementTrailing={{ element: <IconChevronDownSmall aria-hidden /> }}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        data-testid={testId}
        data-error={hasError ? 'true' : 'false'}
        {...(placeholder !== undefined ? { placeholder } : {})}
        {...(disabled !== undefined ? { disabled } : {})}
        {...(hasError !== undefined ? { error: hasError } : {})}
        {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
        {...(isRequired ? { 'aria-required': true } : {})}
        {...(hasError ? { 'aria-invalid': true } : {})}
      />

      {isOpen && (
        <PopoverController
          isOpen={isOpen}
          target={triggerRef}
          close={closePopover}
          onEscKey={closePopover}
          position={PopoverPosition.BottomLeading}
          offset={{ 'bottom-leading': [0, 4] }}
          shouldCloseOnFocusLeave
          enableScrolling
          dataTestId={`${testId}-popover`}
        >
          <Col
            gridGap="dimensions.spacing.200"
            padding="dimensions.spacing.200"
            {...(triggerWidth !== undefined ? { width: `${triggerWidth}px` } : {})}
          >
            {showSearch && (
              <Input
                value={query}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setQuery(event.target.value)
                }
                placeholder={placeholder ?? ''}
                insetElementLeading={{ element: <IconSearchSmall aria-hidden /> }}
                data-testid={`${testId}-search`}
              />
            )}

            {visibleGroups.length === 0 ? (
              <Text color="color.text.secondary" dataTestId={`${testId}-empty`}>
                No matching options
              </Text>
            ) : (
              visibleGroups.map((group) => {
                const scoped = group.values.map((leaf) => leaf.value);
                const master = computeMasterCheckboxState(scoped, values);
                return (
                  <Col
                    key={group.groupName}
                    gridGap="dimensions.spacing.200"
                    dataTestId={`${testId}-group-${group.groupName}`}
                  >
                    <InputWrapper
                      id={`${testId}-group-${group.groupName}`}
                      labelText={group.groupName}
                      labelPosition={LabelPosition.Trailing}
                    >
                      {(inputProps) => (
                        <Checkbox
                          {...inputProps}
                          checked={master.checked}
                          indeterminate={master.indeterminate}
                          onChange={() => toggleGroup(group)}
                          data-testid={`${testId}-group-checkbox-${group.groupName}`}
                        />
                      )}
                    </InputWrapper>

                    {group.values.map((leaf: TagLeafOption) => (
                      <Row key={leaf.value} padding={{ left: 'dimensions.spacing.400' }}>
                        <InputWrapper
                          id={`${testId}-leaf-${leaf.value}`}
                          labelText={leaf.label}
                          labelPosition={LabelPosition.Trailing}
                        >
                          {(inputProps) => (
                            <Checkbox
                              {...inputProps}
                              checked={selectedSet.has(leaf.value)}
                              onChange={() => toggleLeaf(leaf.value)}
                              data-testid={`${testId}-leaf-checkbox-${leaf.label}`}
                            />
                          )}
                        </InputWrapper>
                      </Row>
                    ))}
                  </Col>
                );
              })
            )}
          </Col>
        </PopoverController>
      )}

      {/* Selected tags as dismissible two-line chips (value + group) below the
       *  field — matching the manual flow. Only rendered when a dismiss alt
       *  text is supplied and there is at least one selection. */}
      {chipDismissAltText !== undefined && chipItems.length > 0 && (
        <ChipTokenGroup
          items={chipItems}
          onRemove={toggleLeaf}
          dismissAltText={chipDismissAltText}
          emptyText=""
          disabled={disabled ?? false}
          dataTestId={`${testId}-chips`}
        />
      )}
    </Col>
  );
}
